import React from 'react'

import {
    NoticeBox,
    NoticeBoxColors,
    NoticeBoxVariants,
    VsExclamationIcon,
} from '@octane/spark'
import { FormikAmountField } from 'components/FormikFields'
import FormSubsection from 'components/FormSubsection'
import { FieldKit } from 'containers/FormBuilder'
import { FormikValues, getIn } from 'formik'
import styled from 'styled-components'

const HULL_CONDITION_PREOWNED = 'U'

const VARIANT_CONFIGS = {
    new: {
        sectionTitle: 'Total New Value',
        fieldLabel: 'OEM Invoice Amount',
        noticeContent: (
            <>
                <strong>Invoice amount</strong> is the amount from the
                OEM&apos;s invoice. This includes hull, motor(s), trailer, and
                freight. Do not include sales tax or labor.
            </>
        ),
    },
    preowned: {
        sectionTitle: 'Total Pre-owned Value',
        fieldLabel: 'NADA Wholesale Amount',
        noticeContent: (
            <>
                <strong>NADA wholesale amount</strong> is the total NADA
                wholesale or trade-in value including equipment, hours, and
                deductions. Low retail amount is accepted if wholesale value
                isn&apos;t published. Bookout must be dated within 45 days.
            </>
        ),
    },
}

const AmountContainer = styled.div<{ $hasError?: boolean }>`
    background: ${({ theme, $hasError }) =>
        $hasError
            ? theme.states.highlight.warning.value
            : theme.states.highlight.spotlight.value};
    border-radius: 5px;
    padding: 10px;
    display: flex;
    gap: 20px;
    align-items: flex-start;
`

const FieldWrapper = styled.div`
    width: 170px;
    flex-shrink: 0;
`

const NoticeBoxWrapper = styled.div`
    padding-top: 16px;
`

export const ValidationErrorMessage = styled.span`
    font-weight: 400;
    font-size: 12px;
    color: ${(props) => props.theme.colors.alert.warning};
    margin-top: 5px;
`

interface Props {
    readonly formik: FieldKit<FormikValues>
    readonly prefilled?: boolean
    readonly condition: string
}

export default function TotalAmountSection({
    formik,
    prefilled,
    condition,
}: Readonly<Props>): JSX.Element {
    const isPreowned = condition === HULL_CONDITION_PREOWNED
    const config = isPreowned ? VARIANT_CONFIGS.preowned : VARIANT_CONFIGS.new
    const errorMessage = getIn(formik.errors, 'marine.totalUnitValue')

    const hasError =
        getIn(formik.touched, 'marine.totalUnitValue') && errorMessage

    return (
        <FormSubsection title={config.sectionTitle}>
            <AmountContainer $hasError={hasError}>
                <FieldWrapper>
                    <FormikAmountField
                        name="marine.totalUnitValue"
                        label={config.fieldLabel}
                        prefilled={prefilled}
                        hideErrorMessage={true}
                        placeholder="0"
                    />
                </FieldWrapper>
                <NoticeBoxWrapper>
                    <NoticeBox
                        icon={VsExclamationIcon}
                        title=""
                        color={
                            hasError
                                ? NoticeBoxColors.WARNING
                                : NoticeBoxColors.SPOTLIGHT
                        }
                        variant={NoticeBoxVariants.INLINE}
                    >
                        {config.noticeContent}
                    </NoticeBox>
                </NoticeBoxWrapper>
            </AmountContainer>
            {hasError && (
                <ValidationErrorMessage>{errorMessage}</ValidationErrorMessage>
            )}
        </FormSubsection>
    )
}
